import { useState } from 'react'
import { Group, Stack, Text } from '@mantine/core'
import { TextInput } from '../Common/TextInput'
import { Button } from '../Common/Button'
import { isValidEmail } from '../../utils/emailValidator'

export interface AdhocContactFormProps {
  // Whether the card's + / - toggle has the form expanded. The form renders nothing when
  // closed rather than being unmounted by the card, so in-progress edits survive a collapse
  // and are still there on the next expand (MSD-255).
  open: boolean
  // Prefilled from the unmatched email recipient (the name is often empty —
  // Outlook returns no display name for addresses it can't resolve).
  initialName: string
  initialEmail: string
  saving: boolean
  // Save failure from the API — validation errors are handled per-field here.
  apiError: string | null
  // True when the name or the email — but not both — already belongs to a participant or
  // persisted ad-hoc contact on the claim. That's a warning only: the user confirms with
  // Yes/No and the save still goes through (MSD-257). An exact name+email repeat returns
  // false here on purpose; the proc refuses that pair (Msg 105) and its message arrives
  // as `apiError`, so warning first would just add a step.
  isPartialDuplicate: (name: string, email: string) => boolean
  onSave: (name: string, email: string) => void
  onCancel: () => void
}

// MC1 field length for both name and email.
const MAX_FIELD_LENGTH = 100

// A name has to carry at least one letter, so digits-only entries like "12345" are
// rejected. Names that merely contain a digit still save — claim contacts are often
// companies ("3M", "21st Century Insurance"), so digits alone aren't disqualifying.
const CONTAINS_LETTER = /\p{L}/u

function validateName(name: string): string | null {
  const trimmed = name.trim()
  if (!trimmed) return 'Name is required.'
  if (!CONTAINS_LETTER.test(trimmed)) return 'Enter a valid name — it must include letters, not only numbers.'
  return null
}

function validateEmail(email: string): string | null {
  const trimmed = email.trim()
  if (!trimmed) return 'Email is required.'
  if (!isValidEmail(trimmed)) return 'Enter a valid email address.'
  return null
}

export function AdhocContactForm({
  open,
  initialName,
  initialEmail,
  saving,
  apiError,
  isPartialDuplicate,
  onSave,
  onCancel,
}: Readonly<AdhocContactFormProps>) {
  const [name, setName] = useState(initialName)
  const [email, setEmail] = useState(initialEmail)
  const [nameError, setNameError] = useState<string | null>(null)
  const [emailError, setEmailError] = useState<string | null>(null)
  // Cancel with edits switches to an inline confirmation instead of window.confirm,
  // which is unreliable inside the Outlook add-in iframe.
  const [confirmingDiscard, setConfirmingDiscard] = useState(false)
  // A partial match on the claim's contacts switches to an inline "Save anyway?"
  // confirmation — a warning only, never a block (MSD-257).
  const [confirmingDuplicate, setConfirmingDuplicate] = useState(false)
  // The card's save hook is shared with its other states, so a failure it holds must not
  // show until this form has actually attempted a save.
  const [saveAttempted, setSaveAttempted] = useState(false)

  const isDirty = name !== initialName || email !== initialEmail

  // Discarding has to clear the fields explicitly: the form stays mounted while collapsed
  // (that's what preserves in-progress edits), so without this the discarded values would
  // be back on the next expand.
  const resetFields = () => {
    setName(initialName)
    setEmail(initialEmail)
    setNameError(null)
    setEmailError(null)
    setConfirmingDuplicate(false)
    setSaveAttempted(false)
  }

  const doSave = () => {
    setSaveAttempted(true)
    onSave(name.trim(), email.trim())
  }

  const handleSave = () => {
    const nextNameError = validateName(name)
    const nextEmailError = validateEmail(email)
    setNameError(nextNameError)
    setEmailError(nextEmailError)
    if (nextNameError || nextEmailError) return
    if (isPartialDuplicate(name.trim(), email.trim())) {
      setConfirmingDuplicate(true)
      return
    }
    doSave()
  }

  const handleConfirmDuplicate = () => {
    setConfirmingDuplicate(false)
    doSave()
  }

  const handleCancel = () => {
    if (isDirty) {
      setConfirmingDiscard(true)
      return
    }
    resetFields()
    onCancel()
  }

  const handleDiscard = () => {
    setConfirmingDiscard(false)
    resetFields()
    onCancel()
  }

  // Rendered by the card whether expanded or not, so that collapsing keeps this component
  // mounted and its edits intact (MSD-255). Placed after the hooks, which must always run.
  if (!open) return null

  if (confirmingDuplicate) {
    return (
      <Group justify="space-between" align="center" wrap="nowrap" mt="xs">
        <Text fz="xs">A contact with this name or email already exists. Save anyway?</Text>
        <Group gap="xs" wrap="nowrap">
          <Button label="No" size="xs" variant="outline" onClick={() => setConfirmingDuplicate(false)} />
          <Button label="Yes" size="xs" variant="filled" onClick={handleConfirmDuplicate} />
        </Group>
      </Group>
    )
  }

  if (confirmingDiscard) {
    return (
      <Group justify="space-between" align="center" wrap="nowrap" mt="xs">
        <Text fz="xs">Discard changes?</Text>
        <Group gap="xs" wrap="nowrap">
          <Button label="Keep editing" size="xs" variant="outline" onClick={() => setConfirmingDiscard(false)} />
          <Button label="Discard" size="xs" variant="filled" color="red" onClick={handleDiscard} />
        </Group>
      </Group>
    )
  }

  return (
    <Stack gap="xs" mt="xs">
      <TextInput
        label="Name"
        size="xs"
        withAsterisk
        maxLength={MAX_FIELD_LENGTH}
        value={name}
        error={nameError}
        // Validated on blur as well as on Save, so a bad name is flagged as soon as the
        // user leaves the field instead of only once they try to save.
        onBlur={(event) => setNameError(validateName(event.currentTarget.value))}
        onChange={(event) => {
          setName(event.currentTarget.value)
          setNameError(null)
        }}
      />
      <TextInput
        label="Email"
        size="xs"
        withAsterisk
        maxLength={MAX_FIELD_LENGTH}
        value={email}
        error={emailError}
        onChange={(event) => {
          setEmail(event.currentTarget.value)
          setEmailError(null)
        }}
      />
      {saveAttempted && apiError && (
        <Text fz="xs" c="red">
          {apiError}
        </Text>
      )}
      <Group justify="flex-end" gap="xs" wrap="nowrap">
        <Button label="Cancel" size="xs" variant="outline" onClick={handleCancel} />
        <Button label="Save" size="xs" variant="filled" loading={saving} onClick={handleSave} />
      </Group>
    </Stack>
  )
}
